import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from './DashboardHeader';
import MaterialSummary from './MaterialSummary';
import Layout from '../../../components/layout/AppLayout';
import ExceptionView from './ExceptionView';
import Loader from '../../../../shared/components/ui/Loader';
import ErrorScreen from '../../../../shared/components/ui/ErrorScreen';
import { getOperationsMonthlyInventoryStats, getAllCategories } from '../../../services/api/implementation/impl';
import { useProjectsList } from '../../../../../contexts/ProjectsContext';
import { useQuery } from '@tanstack/react-query';

const getErrorMessage = (error) => {
    if (!error) return 'Failed to load monthly inventory data.';
    const msg = error?.response?.data?.message || error?.message;
    if (msg && typeof msg === 'string') {
        const reportRunDateMatch = msg.match(/reportRunDate must be on or after (\d{4}-\d{2}-\d{2}) for MONTHLY_INVENTORY reports/i);
        if (reportRunDateMatch) {
            const minDate = reportRunDateMatch[1];
            return `Monthly inventory report is only available from ${minDate} onwards. Please select a month that ends on or after ${minDate} to view the report.`;
        }
        return msg;
    }
    return 'Failed to load monthly inventory data. Please try again.';
};

const InventoryMonthlyReports = () => {
    const navigate = useNavigate();
    const { projectsForDropdown, projectsError, refetchProjects } = useProjectsList();
    const projects = projectsForDropdown?.length > 0 ? projectsForDropdown : [{ label: 'No Project', id: null, value: null }];

    const [activeView, setActiveView] = useState('Summary'); // Summary, Risk Overview
    const [summaryTab, setSummaryTab] = useState('Progress Overview');
    const [currentProject, setCurrentProject] = useState(projects[0]?.label || 'No Project');
    // Default to first day of previous month (current/future months not selectable)
    const [selectedDate, setSelectedDate] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth() - 1, 1);
    });
    const [loading, setLoading] = useState(false);
    const [inventoryMonthlyData, setInventoryMonthlyData] = useState(null);
    const [inventoryError, setInventoryError] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState(''); // '', 'excellent', 'good', 'moderate', 'critical'

    const fetchMonthlyInventoryStats = useCallback(async () => {
        const projectObj = projectsForDropdown?.find((p) => p.label === currentProject);
        const projectId = projectObj?.id;
        if (!projectId) return;

        setLoading(true);
        setInventoryError(null);
        try {
            // Selected Feb → pass 1 Mar; selected Jan → pass 1 Feb (always 1st of next month; Dec → 1 Jan next year)
            const nextMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1);
            const y = nextMonth.getFullYear();
            const m = String(nextMonth.getMonth() + 1).padStart(2, '0');
            const reportRunDate = `${y}-${m}-01`;
            const response = await getOperationsMonthlyInventoryStats(projectId, reportRunDate);
            if (response?.data) {
                setInventoryMonthlyData(response.data);
            } else {
                setInventoryMonthlyData(null);
            }
        } catch (error) {
            console.error('Failed to fetch monthly inventory stats', error);
            setInventoryMonthlyData(null);
            setInventoryError(getErrorMessage(error));
        } finally {
            setLoading(false);
        }
    }, [currentProject, selectedDate, projectsForDropdown]);

    useEffect(() => {
        if (projects?.length > 0 && projects[0]?.id !== null && currentProject === 'No Project') {
            setCurrentProject(projects[0]?.label);
        }
    }, [projects, currentProject]);

    const { data: categoriesResponse } = useQuery({
        queryKey: ['AllCategories'],
        queryFn: () => getAllCategories(1, 500)
    });
    const categoriesForDropdown =
        categoriesResponse?.data?.map((item) => ({
            label: item.name || '',
            id: item.id,
            value: item.id
        })) || [];

    useEffect(() => {
        fetchMonthlyInventoryStats();
    }, [fetchMonthlyInventoryStats]);

    const handleRetry = useCallback(async () => {
        setInventoryError(null);
        try {
            if (projectsError) await refetchProjects();
            await fetchMonthlyInventoryStats();
        } catch (err) {
            console.error('Retry failed', err);
            setInventoryError(getErrorMessage(err));
        }
    }, [projectsError, refetchProjects, fetchMonthlyInventoryStats]);

    // Critical = stock_criticality < 100 (same threshold as MaterialCard)
    const criticalItemsCount = (inventoryMonthlyData?.monthlyInventory?.items ?? []).filter(
        (item) => (Number(item.stock_criticality) || 0) < 100
    ).length;

    const reportError = projectsError || inventoryError;
    const errorMessage = projectsError
        ? 'Failed to load projects. Please check your connection and try again.'
        : (inventoryError || 'Failed to load monthly inventory data. Please try again.');

    return (
        <Layout>
            <Loader isVisible={loading} />
            <div className="bg-[#F8FAFC] min-h-screen font-sans">
                <DashboardHeader
                    activeTab={activeView}
                    setActiveTab={setActiveView}
                    projectName={currentProject}
                    onProjectChange={setCurrentProject}
                    date={selectedDate}
                    onDateChange={setSelectedDate}
                    projects={projects}
                    criticalItemsCount={criticalItemsCount}
                    onViewSample={() => navigate('/builder/inventory/monthly-report/sample')}
                    categories={categoriesForDropdown}
                    categoryFilter={categoryFilter}
                    statusFilter={statusFilter}
                    onCategoryChange={setCategoryFilter}
                    onStatusChange={setStatusFilter}
                />

                <div className='p-6'>

                    {/* Error screen when projects or inventory API fails */}
                    {reportError && (
                        <ErrorScreen
                            title="Unable to Load Monthly Inventory Report"
                            message={errorMessage}
                            onRetry={handleRetry}
                            isRetrying={loading}
                        />
                    )}

                    {/* Content Area - hide when any API error */}
                    {!reportError && (
                        <div className="animate-in fade-in duration-500">
                            {activeView === 'Summary' && (
                                <>
                                    {summaryTab === 'Progress Overview' && (
                                        <MaterialSummary
                                            monthlyInventory={inventoryMonthlyData?.monthlyInventory}
                                            categoryFilter={categoryFilter}
                                            statusFilter={statusFilter}
                                        />
                                    )}
                                    {summaryTab === 'Minor Delays' && (
                                        <div className="flex items-center justify-center p-20 bg-white rounded-lg border border-gray-200 shadow-sm border-dashed">
                                            <p className="text-gray-400 font-medium">No minor delays reported.</p>
                                        </div>
                                    )}
                                </>
                            )}

                            {activeView === 'Risk Overview' && (
                                <ExceptionView
                                    procurementForesight={inventoryMonthlyData?.procurementForesight}
                                    emergencyPurchases={inventoryMonthlyData?.emergencyPurchases}
                                    excessConsumption={inventoryMonthlyData?.excessConsumption}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default InventoryMonthlyReports;
